import { Fragment, useEffect, useId, useState, type ReactNode } from 'react'
import { CircleAlert } from 'lucide-react'
import { Button } from '@/components/ui'
import type { ButtonProps } from '@/components/ui/Button'
import { cn } from '@/utils'

/**
 * SA-1331: the one task action row. A work action that was pressed spins while the
 * page is busy; every other button keeps its look and ignores a click. A held-back
 * button is disabled and says why in a line under the row, never a native title.
 * A press that busy does not follow within `PRESS_WINDOW_MS` is forgotten, so a later
 * busy from elsewhere (the page's Claim) spins nothing (SA-1333).
 */
export const PRESS_WINDOW_MS = 1000

export interface TaskAction {
  id: string
  label: ReactNode
  onClick: () => void
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  blocked?: string | null
  /** The words in `blocked` that run `onClick`; absent from the sentence, the link follows it. Blank words are no link. */
  blockedLink?: { text: string; onClick: () => void }
  description?: string
  kind?: 'work' | 'local'
  icon?: ReactNode
  className?: string
  attrs?: Record<`data-${string}`, string>
}

export interface TaskActionsProps {
  actions: readonly TaskAction[]
  busy: boolean
  note?: ReactNode
  /** SA-1327: a quiet line drawn after the buttons, in the same row. */
  aside?: ReactNode
  className?: string
  attrs?: Record<`data-${string}`, string>
}

export function TaskActions({ actions, busy, note, aside, className, attrs }: TaskActionsProps) {
  const base = useId()
  const [pressed, setPressed] = useState<string | null>(null)
  const [prevBusy, setPrevBusy] = useState(busy)
  // The pressed record clears on busy's falling edge, so busy arriving a render
  // after the click (React Query notifies on a timeout) still lights the button.
  if (busy !== prevBusy) {
    setPrevBusy(busy)
    if (prevBusy && !busy) setPressed(null)
  }
  useEffect(() => {
    if (pressed === null || busy) return
    const forget = setTimeout(() => setPressed(null), PRESS_WINDOW_MS)
    return () => clearTimeout(forget)
  }, [pressed, busy])

  const reasons: string[] = []
  const links = new Map<string, NonNullable<TaskAction['blockedLink']>>()
  for (const a of actions) {
    if (a.blocked && !reasons.includes(a.blocked)) {
      reasons.push(a.blocked)
      if (a.blockedLink) links.set(a.blocked, a.blockedLink)
    }
  }
  const reasonId = (reason: string) => `${base}-reason-${reasons.indexOf(reason)}`
  const descId = (i: number) => `${base}-desc-${i}`

  const click = (a: TaskAction) => {
    if (busy) return
    if (a.kind !== 'local') setPressed(a.id)
    a.onClick()
  }

  return (
    <div>
      <div {...attrs} className={cn('flex flex-wrap items-center gap-2', className)}>
        {actions.map((a, i) => {
          const blocked = a.blocked ? a.blocked : null
          const spinning = busy && pressed === a.id && a.kind !== 'local'
          const describedBy = [a.description ? descId(i) : null, blocked ? reasonId(blocked) : null]
            .filter(Boolean)
            .join(' ')
          return (
            <Fragment key={a.id}>
              <Button
                {...a.attrs}
                type="button"
                variant={a.variant ?? 'default'}
                size={a.size ?? 'md'}
                isLoading={spinning}
                disabled={blocked !== null}
                aria-describedby={describedBy || undefined}
                className={cn(spinning && 'disabled:opacity-100', a.className)}
                onClick={() => click(a)}
              >
                {a.icon}
                {a.label}
              </Button>
              {a.description ? (
                <span id={descId(i)} className="sr-only">
                  {a.description}
                </span>
              ) : null}
            </Fragment>
          )
        })}
        {aside ? (
          <span data-slot="task-actions-aside" className="text-xs text-muted-foreground">
            {aside}
          </span>
        ) : null}
      </div>
      {reasons.map((reason) => (
        <p key={reason} id={reasonId(reason)} role="status" className="mt-2 flex items-center gap-1.5 text-xs text-warning-strong">
          <CircleAlert className="size-3.5 shrink-0" aria-hidden="true" />
          <span>{reasonWords(reason, links.get(reason))}</span>
        </p>
      ))}
      {note ? <p className="mt-2 text-xs text-muted-foreground">{note}</p> : null}
    </div>
  )
}

const LINK_CLASS = 'rounded-sm underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'

function reasonWords(reason: string, link: TaskAction['blockedLink']): ReactNode {
  if (!link || !link.text.trim()) return reason
  const button = (
    <button type="button" className={LINK_CLASS} onClick={link.onClick}>
      {link.text}
    </button>
  )
  const at = reason.indexOf(link.text)
  if (at < 0) return <>{reason} {button}</>
  return (
    <>
      {reason.slice(0, at)}
      {button}
      {reason.slice(at + link.text.length)}
    </>
  )
}
